import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Menu } from 'antd';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { MenuItem } from 'app/components/commons/MenuCustom/MenuCustom';

import styles from './ScrollableMenu.module.scss';

type ScrollableMenuProps<NameType extends string | number> = {
  menuItems: MenuItem<NameType>[];
  selectedMenu: MenuItem<NameType>;
  setSelectedMenu: (menuItem: MenuItem<NameType>) => void;
};

const SCROLL_STEP = 200;

export const ScrollableMenu = <NameType extends string | number>({
  menuItems,
  selectedMenu,
  setSelectedMenu,
}: ScrollableMenuProps<NameType>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const selectedIndex = menuItems.findIndex(
    (item) => item.name === selectedMenu.name
  );

  const updateScrollState = useCallback(() => {
    const el = containerRef.current;

    if (!el) {
      return;
    }

    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = containerRef.current;

    if (!el) {
      return;
    }

    updateScrollState();

    const observer = new ResizeObserver(updateScrollState);

    observer.observe(el);
    el.addEventListener('scroll', updateScrollState, { passive: true });

    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', updateScrollState);
    };
  }, [updateScrollState, menuItems]);

  const scroll = (direction: 'left' | 'right') => {
    containerRef.current?.scrollBy({
      left: direction === 'left' ? -SCROLL_STEP : SCROLL_STEP,
      behavior: 'smooth',
    });
  };

  return (
    <div className={styles.wrapper}>
      {canScrollLeft && (
        <div
          className={classNames(
            styles.arrowContainer,
            styles.arrowLeftContainer
          )}
        >
          <button
            type="button"
            className={classNames(styles.arrowLeft, styles.arrow)}
            onClick={() => scroll('left')}
            aria-label="Scroll left"
          >
            <LeftOutlined />
          </button>
        </div>
      )}

      <div ref={containerRef} className={styles.scrollContainer}>
        <Menu
          mode="horizontal"
          disabledOverflow
          selectedKeys={[selectedIndex.toString()]}
          onClick={({ key }) => {
            const item = menuItems[+key];

            if (item && !item.disabled) {
              setSelectedMenu(item);
            }
          }}
          items={menuItems.map((item, index) => ({
            key: index.toString(),
            disabled: item.disabled,
            label: item.label ?? item.name,
          }))}
        />
      </div>

      {canScrollRight && (
        <div
          className={classNames(
            styles.arrowContainer,
            styles.arrowRightContainer
          )}
        >
          <button
            type="button"
            className={classNames(styles.arrowRight, styles.arrow)}
            onClick={() => scroll('right')}
            aria-label="Scroll right"
          >
            <RightOutlined />
          </button>
        </div>
      )}
    </div>
  );
};
